'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgentLineFold } from '@/app/components/icons/AgentLineFold';
import { AgentLineGenerating } from '@/app/components/icons/AgentLineGenerating';
import { AgentLineUnfold } from '@/app/components/icons/AgentLineUnfold';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { ProductLineDuration } from '@/app/components/icons/ProductLineDuration';
import { ProductLineSubagent } from '@/app/components/icons/ProductLineSubagent';
import { SuggestFillSuccee } from '@/app/components/icons/SuggestFillSuccee';
import { cn } from '@/app/utils';
import type { SubtaskPhase } from '../../../types';

interface SubtaskPanelProps {
  phases: SubtaskPhase[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
}

const PhaseStatusIcon = ({ status }: { status: string }) => {
  if (status === 'completed') {
    return <SuggestFillSuccee className="size-[14px] shrink-0" />;
  }
  if (status === 'running') {
    return <AgentLineGenerating className="size-[14px] shrink-0" />;
  }
  return <ProductLineDuration className="size-[14px] shrink-0 light:text-[#111111] text-system-white36" />;
};

export const SubtaskPanel = ({
  phases,
  expanded,
  onExpandedChange,
  scrollContainerRef,
  className,
}: SubtaskPanelProps) => {
  const t = useTranslations();
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [overflowPhases, setOverflowPhases] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const textNodesRef = useRef<Map<string, HTMLSpanElement>>(new Map());
  const rafRef = useRef<number>(0);

  const recheckOverflow = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const next = new Set<string>();
      for (const [key, node] of textNodesRef.current) {
        if (node.scrollWidth > node.clientWidth) next.add(key);
      }
      setOverflowPhases((prev) => {
        if (prev.size === next.size && [...prev].every((k) => next.has(k))) return prev;
        return next;
      });
    });
  }, []);

  const textRefCallback = useCallback((node: HTMLSpanElement | null, phaseKey: string) => {
    if (node) {
      textNodesRef.current.set(phaseKey, node);
    } else {
      textNodesRef.current.delete(phaseKey);
    }
  }, []);

  useEffect(() => {
    if (expanded) recheckOverflow();
  }, [expanded, expandedPhases, phases, recheckOverflow]);

  useEffect(() => {
    if (!expanded) return;
    const container = listRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => recheckOverflow());
    observer.observe(container);
    return () => observer.disconnect();
  }, [expanded, recheckOverflow]);

  const total = phases.length;
  const completed = phases.filter((p) => p.phaseStatus === 'completed').length;
  const running = phases.filter((p) => p.phaseStatus === 'running').length;

  useEffect(() => {
    if (expanded && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [expanded, phases]);

  if (total === 0) return null;

  return (
    <div
      className={cn(
        'relative z-10 mx-3 overflow-hidden rounded-tl-12 rounded-tr-12 border-system-white08 border-x border-t bg-[#26292B] light:bg-[#F4F4FA]',
        className,
      )}
    >
      {/* header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <ProductLineSubagent className="size-[17px] shrink-0 text-system-white" />
          <span className="truncate text-sm text-system-white">
            {t.rich(completed === total ? 'claw.chat.subtask.summaryDone' : 'claw.chat.subtask.summary', {
              completed,
              running,
              total,
              num: (chunks) => <span className="text-system-white64">{chunks}</span>,
            })}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            const nextExpanded = !expanded;
            onExpandedChange(nextExpanded);
            if (nextExpanded && scrollContainerRef?.current) {
              requestAnimationFrame(() => {
                const container = scrollContainerRef.current;
                const list = listRef.current;
                if (container && list) {
                  container.scrollTop += list.offsetHeight;
                }
              });
            }
          }}
          className="flex h-[17px] w-[17px] shrink-0 items-center justify-center text-system-white36 hover:text-system-white48"
          aria-label={expanded ? t('claw.chat.subtask.collapse') : t('claw.chat.subtask.expand')}
        >
          {expanded ? (
            <AgentLineFold className="h-[17px] w-[17px]" />
          ) : (
            <AgentLineUnfold className="h-[17px] w-[17px]" />
          )}
        </button>
      </div>

      {/* list */}
      {expanded && (
        <div className="px-2 pb-2">
          <div
            ref={listRef}
            className="max-h-[220px] select-none overflow-y-auto rounded-t-8 bg-black/[0.24] light:bg-[rgba(255,255,255,0.64)] px-4 py-3"
          >
            {phases.map((phase) => {
              const isRunning = phase.phaseStatus === 'running';
              const hasDescription = !!phase.phaseDescription;
              const phaseKey = `${phase.coordinatorId}-${phase.phaseId}`;
              const isPhaseExpanded = expandedPhases.has(phaseKey);
              const isOverflow = overflowPhases.has(phaseKey);
              const canExpand = !isRunning && hasDescription && (isOverflow || isPhaseExpanded);

              const togglePhase = () => {
                setExpandedPhases((prev) => {
                  const next = new Set(prev);
                  if (next.has(phaseKey)) next.delete(phaseKey);
                  else next.add(phaseKey);
                  return next;
                });
              };

              return (
                <div key={phaseKey} className="group/phase flex flex-col">
                  <div
                    className={cn('flex items-start gap-3 py-1', canExpand && 'cursor-pointer')}
                    onClick={canExpand ? togglePhase : undefined}
                  >
                    <div className="mt-[3px] flex w-[14px] shrink-0 items-center justify-center">
                      <PhaseStatusIcon status={phase.phaseStatus} />
                    </div>
                    <span
                      ref={(node) => textRefCallback(node, phaseKey)}
                      className={cn(
                        'min-w-0 flex-1 text-sm text-system-white32',
                        isRunning &&
                          'bg-gradient-to-r from-white light:from-[#111111] light:to-[#111111]/40 to-white/40 bg-clip-text text-transparent',
                        !isRunning && !isPhaseExpanded && 'truncate',
                      )}
                    >
                      <span className={cn(!isRunning && 'text-system-white64')}>{phase.phaseName}</span>
                      {hasDescription && <>: {phase.phaseDescription}</>}
                    </span>
                    {canExpand && (
                      <div className="mt-[3px] shrink-0">
                        <BaseLineRight
                          className={cn(
                            'size-[14px] shrink-0 text-system-white36 transition-all duration-300 ease-in-out',
                            isPhaseExpanded
                              ? 'rotate-90 opacity-100'
                              : 'rotate-0 opacity-0 group-hover/phase:opacity-100',
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
